import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, CalendarClock, Send } from "lucide-react";



// ---------------------------------------------------------------------------
// Iteration 42 — Admin: Sunday-9AM KSA Weekly Summary status & on-demand sender
// ---------------------------------------------------------------------------
const AdminWeeklySummaryPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/weekly-summary/status");
      setStatus(r.data || null);
    } catch (err) { console.warn("admin: weekly-summary status failed", err); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!window.confirm("سيتم إرسال الملخص الأسبوعي إلى رقم المستشار عبر واتساب. هل تريد المتابعة؟")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/weekly-summary/run-now");
      const delivery = data?.delivery;
      const m = data?.metrics || {};
      if (delivery?.ok) {
        toast.success(`تم إرسال الملخص الأسبوعي عبر واتساب (${m.completed_sessions} جلسة، ${m.revenue_sar?.toLocaleString?.() ?? 0} ر.س).`);
      } else {
        toast.warning(`تم تسجيل الملخص — لم يتم الإرسال الفعلي: ${delivery?.error || "—"}`);
      }
      load();
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر الإرسال");
    } finally {
      setBusy(false);
    }
  };

  const preview = status?.preview || {};

  return (
    <div data-testid="admin-weekly-summary" className="card-warm p-5" dir="rtl">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-[#A67C41]" />
        <h3 className="font-heading text-xl text-[#222222]">الملخص الأسبوعي للمستشار</h3>
      </div>
      <p className="text-sm text-[#5E5C58] leading-relaxed">
        يُرسل تلقائيًا كل <strong>يوم أحد الساعة ٩:٠٠ صباحًا (توقيت الرياض)</strong>{" "}
        إلى رقم واتساب المستشار. يحتوي على ملخص الأسبوع المنصرم (الجلسات المكتملة،
        الإيرادات، العملاء الجدد، أفضل ٣ عملاء وفاءً، وجلسات الأسبوع القادم).
      </p>

      {status && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">المستلم</div>
            <div dir="ltr" className="font-mono text-[#222222]">
              {status?.recipient || "—"}
            </div>
          </div>
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">الفترة (الأسبوع المنصرم)</div>
            <div className="text-[#222222] font-mono" dir="ltr">
              {preview.week_range_human || "—"}
            </div>
          </div>
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">الجلسات المكتملة</div>
            <div className="text-[#0A4D3C] text-lg font-semibold" data-testid="weekly-completed-sessions">
              {preview.completed_sessions ?? 0}
            </div>
          </div>
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">الإيرادات</div>
            <div className="text-[#0A4D3C] text-lg font-semibold" data-testid="weekly-revenue">
              {(preview.revenue_sar || 0).toLocaleString()} ر.س
            </div>
          </div>
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">عملاء جدد</div>
            <div className="text-[#222222] font-semibold">{preview.new_clients ?? 0}</div>
          </div>
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">جلسات الأسبوع القادم</div>
            <div className="text-[#222222] font-semibold">{preview.upcoming_next_week ?? 0}</div>
          </div>
        </div>
      )}

      {preview?.top_loyal?.length > 0 && (
        <div className="mt-4 rounded-xl bg-white border border-[#E5E1D8] overflow-hidden">
          <div className="bg-[#F6F4EE] px-4 py-2 text-xs text-[#5E5C58] font-semibold">
            🏆 أعلى {Math.min(3, preview.top_loyal.length)} عملاء وفاءً (آخر ٣٠ يوم)
          </div>
          <table className="w-full text-sm">
            <tbody>
              {preview.top_loyal.slice(0, 3).map((t, idx) => (
                <tr key={`top-${t.name}-${t.count}`} className="border-t border-[#E5E1D8]">
                  <td className="p-2 ps-4 w-8 text-xs text-[#8B8780]">#{idx + 1}</td>
                  <td className="p-2">{t.name}</td>
                  <td className="p-2 pe-4 text-xs text-[#5E5C58]">{t.count} معاملة</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status?.last_run && (
        <div className="mt-4 rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
          <div className="text-xs text-[#8B8780] mb-1">آخر إرسال</div>
          <div className="text-[#222222]">
            {status.last_run.week_tag || "—"}
            {" · "}
            <span className="text-xs">{status.last_run.delivery || "—"}</span>
          </div>
        </div>
      )}

      <div className="mt-5">
        <Button
          type="button"
          data-testid="weekly-summary-run-now-btn"
          onClick={runNow}
          disabled={busy}
          className="rounded-full bg-[#A67C41] hover:bg-[#8C6239] text-white"
        >
          <Send className="w-3.5 h-3.5 ms-2" />
          {busy ? "جاري الإرسال..." : "إرسال الآن"}
        </Button>
      </div>
    </div>
  );
};



// ---------------------------------------------------------------------------
// Iteration 41 — Admin: 5PM-KSA Daily Digest status & on-demand sender
// ---------------------------------------------------------------------------
const AdminDailyDigestPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/daily-digest/status");
      setStatus(r.data || null);
    } catch (err) { console.warn("admin: daily-digest status failed", err); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/daily-digest/run-now");
      const sessions = data?.sessions ?? 0;
      const emailDelivery = data?.email_delivery;  // "sent" | "log_only" | "error" | "skipped" | undefined
      if (sessions === 0) {
        toast.info("لا توجد جلسات غدًا لإرسالها");
      } else if (emailDelivery === "sent" || emailDelivery === "log_only") {
        // log_only = email infra accepted the call but Resend key missing in preview;
        // in production both paths reach the inbox the same way.
        toast.success("تم إرسال ملخص جلسات الغد إلى البريد الإلكتروني");
      } else {
        toast.error("تعذر إرسال ملخص جلسات الغد، حاول مرة أخرى");
      }
      load();
    } catch (e) {
      // Never expose the raw email-provider error to the admin UI.
      console.warn("daily-digest run-now failed", e);
      toast.error("تعذر إرسال ملخص جلسات الغد، حاول مرة أخرى");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-daily-digest" className="card-warm p-5" dir="rtl">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="w-5 h-5 text-[#0A4D3C]" />
        <h3 className="font-heading text-xl text-[#222222]">الملخص اليومي للمستشار</h3>
      </div>
      <p className="text-sm text-[#5E5C58] leading-relaxed">
        يُرسل تلقائيًا كل يوم الساعة <strong>٥:٠٠ مساءً (توقيت الرياض)</strong>{" "}
        إلى البريد الإلكتروني للمستشارة، ويحتوي على جلسات الغد (الوقت، اسم العميلة،
        رقم Botim، الخدمة). إذا لم تكن هناك جلسات غدًا، لا يُرسل شيء.
      </p>

      {status && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">المُستلِم (البريد الإلكتروني)</div>
            <div dir="ltr" className="font-mono text-[#222222] truncate">
              {status?.recipient_email || status?.recipient || "—"}
            </div>
          </div>
          <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58]">
            <div className="text-xs text-[#8B8780] mb-1">جلسات الغد</div>
            <div className="text-[#222222] font-semibold" data-testid="daily-digest-tomorrow-count">
              {status?.tomorrow_sessions_count ?? 0} جلسة
            </div>
          </div>
          {status?.last_run && (
            <div className="rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-sm text-[#5E5C58] sm:col-span-2">
              <div className="text-xs text-[#8B8780] mb-1">آخر إرسال</div>
              <div className="text-[#222222]">
                {status.last_run.day_tag || "—"}
                {" · "}
                <span className="text-xs">
                  {status.last_run.sessions ?? 0} جلسة · {status.last_run.delivery || "—"}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {status?.tomorrow_sessions_preview?.length > 0 && (
        <div className="mt-4 rounded-xl bg-white border border-[#E5E1D8] overflow-hidden">
          <div className="bg-[#F6F4EE] px-4 py-2 text-xs text-[#5E5C58] font-semibold">
            معاينة ملخص الغد ({status.tomorrow_sessions_preview.length})
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-[#5E5C58]">
              <tr>
                <th className="text-start p-2 ps-4">الوقت</th>
                <th className="text-start p-2">العميل</th>
                <th className="text-start p-2">Botim</th>
                <th className="text-start p-2 pe-4">الخدمة</th>
              </tr>
            </thead>
            <tbody>
              {status.tomorrow_sessions_preview.map((r) => (
                <tr key={r.booking_id || `${r.when_short}-${r.client_name}`} className="border-t border-[#E5E1D8]">
                  <td className="p-2 ps-4 font-mono">{r.when_short}</td>
                  <td className="p-2">{r.client_name}</td>
                  <td className="p-2 font-mono text-xs" dir="ltr">{r.client_phone_botim}</td>
                  <td className="p-2 pe-4">{r.service_ar}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-5">
        <Button
          type="button"
          data-testid="daily-digest-run-now-btn"
          onClick={runNow}
          disabled={busy}
          className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
        >
          <Send className="w-3.5 h-3.5 ms-2" />
          {busy ? "جاري الإرسال..." : "إرسال الآن"}
        </Button>
      </div>
    </div>
  );
};

export { AdminWeeklySummaryPanel, AdminDailyDigestPanel };
